import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import { useProfile } from './ProfileContext';
import { supabase } from './supabaseClient';
import AppNav from './AppNav';
import './SettingsPage.css';

const THEME_OPTIONS = [
  {
    value: 'dark',
    label: 'Always Dark',
    desc: 'Navy and gold — the default VetQualified experience.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
      </svg>
    ),
  },
  {
    value: 'light',
    label: 'Always Light',
    desc: 'Clean white background for bright environments.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="5"/>
        <line x1="12" y1="1"  x2="12" y2="3"/>
        <line x1="12" y1="21" x2="12" y2="23"/>
        <line x1="4.22" y1="4.22"  x2="5.64" y2="5.64"/>
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
        <line x1="1"  y1="12" x2="3"  y2="12"/>
        <line x1="21" y1="12" x2="23" y2="12"/>
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
      </svg>
    ),
  },
  {
    value: 'auto',
    label: 'Device Settings',
    desc: 'Follows your device\'s system appearance automatically.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2"/>
        <line x1="8" y1="21" x2="16" y2="21"/>
        <line x1="12" y1="17" x2="12" y2="21"/>
      </svg>
    ),
  },
];

export default function SettingsPage() {
  const { theme, setTheme } = useTheme();
  const { profile } = useProfile();

  const initials = `${profile.firstName?.[0] || ''}${profile.lastName?.[0] || ''}`.toUpperCase() || '?';

  // ── Password change ──────────────────────────────────────────
  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwStatus,        setPwStatus]        = useState('idle'); // 'idle' | 'updating' | 'success' | 'error'
  const [pwError,         setPwError]         = useState('');

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setPwError('');

    if (!newPassword || !confirmPassword) {
      setPwStatus('error');
      setPwError('Both password fields are required.');
      return;
    }
    if (newPassword.length < 8) {
      setPwStatus('error');
      setPwError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwStatus('error');
      setPwError('Passwords do not match.');
      return;
    }

    setPwStatus('updating');
    const { error } = await supabase.auth.updateUser({ password: newPassword });

    if (error) {
      setPwStatus('error');
      setPwError(error.message || 'Could not update password. Please try again.');
      return;
    }

    setPwStatus('success');
    setNewPassword('');
    setConfirmPassword('');
    setTimeout(() => setPwStatus('idle'), 3000);
  };

  return (
    <div className="settings-root">
      <AppNav />

      <div className="settings-shell">

        {/* ── LEFT: nav menu ── */}
        <aside className="settings-left">
          <div className="settings-profile-mini">
            <div className="settings-mini-avatar">
              {profile.avatarUrl
                ? <img src={profile.avatarUrl} alt="avatar" className="settings-mini-avatar-img" />
                : initials
              }
            </div>
            <div>
              <div className="settings-mini-name">{profile.firstName} {profile.lastName}</div>
              <a href="/dashboard" className="settings-mini-link">View profile</a>
            </div>
          </div>

          <nav className="settings-nav">
            <div className="settings-nav-section-label">Account</div>
            <a href="/settings" className="settings-nav-item active">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="4"/>
                <path d="M20 21a8 8 0 10-16 0"/>
              </svg>
              Account Preferences
            </a>
            <a href="/dashboard" className="settings-nav-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              Privacy &amp; Visibility
            </a>
            <a href="/dashboard" className="settings-nav-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 01-3.46 0"/>
              </svg>
              Notifications
            </a>
          </nav>
        </aside>

        {/* ── RIGHT: settings content ── */}
        <main className="settings-main">
          <h1 className="settings-page-title">Account Preferences</h1>

          {/* Display section */}
          <div className="settings-section">
            <div className="settings-section-header">
              <h2 className="settings-section-title">Display</h2>
              <p className="settings-section-desc">Choose how VetQualified looks on this device.</p>
            </div>

            <div className="settings-card">
              <div className="settings-card-label">Dark mode</div>
              <div className="settings-theme-options">
                {THEME_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    className={`settings-theme-btn ${theme === opt.value ? 'active' : ''}`}
                    onClick={() => setTheme(opt.value)}
                  >
                    <div className="settings-theme-icon">{opt.icon}</div>
                    <div className="settings-theme-text">
                      <div className="settings-theme-label">{opt.label}</div>
                      <div className="settings-theme-desc">{opt.desc}</div>
                    </div>
                    <div className={`settings-theme-radio ${theme === opt.value ? 'selected' : ''}`}>
                      {theme === opt.value && <div className="settings-theme-radio-dot" />}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Profile section */}
          <div className="settings-section">
            <div className="settings-section-header">
              <h2 className="settings-section-title">Profile</h2>
              <p className="settings-section-desc">Manage your public-facing information.</p>
            </div>
            <div className="settings-card">
              <a href="/dashboard" className="settings-row-link">
                <div className="settings-row-left">
                  <div className="settings-row-label">Edit Profile</div>
                  <div className="settings-row-desc">Update your military background, career goals, and bio.</div>
                </div>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </a>
              <div className="settings-row-divider" />
              <a href="/dashboard" className="settings-row-link">
                <div className="settings-row-left">
                  <div className="settings-row-label">Privacy Dashboard</div>
                  <div className="settings-row-desc">Control who sees your rank, clearance, and contact info.</div>
                </div>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </a>
            </div>
          </div>

          <p><Link to="/privacy">Privacy, data requests &amp; reporting a concern</Link></p>
          {/* Account section */}
          <div className="settings-section">
            <div className="settings-section-header">
              <h2 className="settings-section-title">Account</h2>
              <p className="settings-section-desc">Manage your login and security settings.</p>
            </div>
            <div className="settings-card">
              <div className="settings-row-link" style={{ cursor: 'default' }}>
                <div className="settings-row-left">
                  <div className="settings-row-label">Email Address</div>
                  <div className="settings-row-desc">Managed through your login provider.</div>
                </div>
              </div>
              <div className="settings-row-divider" />
              <div className="settings-pw-row">
                <div className="settings-pw-header">
                  <div className="settings-row-label">Password</div>
                  <div className="settings-row-desc">Update the password used to sign in to VetQualified. Minimum 8 characters.</div>
                </div>
                <form className="settings-pw-form" onSubmit={handlePasswordUpdate}>
                  <input
                    type="password"
                    className="settings-pw-input"
                    placeholder="New password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    disabled={pwStatus === 'updating'}
                  />
                  <input
                    type="password"
                    className="settings-pw-input"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    disabled={pwStatus === 'updating'}
                  />
                  <button
                    type="submit"
                    className="settings-pw-btn"
                    disabled={pwStatus === 'updating'}
                  >
                    {pwStatus === 'updating' ? 'Updating…' : 'Update Password'}
                  </button>
                  {pwStatus === 'success' && (
                    <div className="settings-pw-msg success">Password updated successfully.</div>
                  )}
                  {pwStatus === 'error' && pwError && (
                    <div className="settings-pw-msg error">{pwError}</div>
                  )}
                </form>
              </div>
            </div>
          </div>

        </main>
      </div>
    </div>
  );
}
